/**
 * Accessibility Enhancements Component
 * Provides skip links, live regions, and other a11y features
 */

import { useEffect, useState } from 'react';

export function A11yEnhancements() {
  const [announcements, setAnnouncements] = useState<string>('');

  // Expose method to announce to screen readers
  useEffect(() => {
    // Global announce function
    (window as any).announceToScreenReader = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
      setAnnouncements(message);
      
      // Clear after announcement
      setTimeout(() => setAnnouncements(''), 1000);
    };

    return () => {
      delete (window as any).announceToScreenReader;
    };
  }, []);

  const handleSkipToMain = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus();
      mainContent.scrollIntoView();
    }
  };

  const handleSkipToNav = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const nav = document.getElementById('main-navigation');
    if (nav) {
      const firstLink = nav.querySelector('a, button');
      if (firstLink instanceof HTMLElement) {
        firstLink.focus();
      }
    }
  };

  return (
    <>
      {/* Skip Links - Hidden but accessible via keyboard */}
      <div className="sr-only focus-within:not-sr-only">
        <a
          href="#main-content"
          onClick={handleSkipToMain}
          className="fixed top-4 left-4 z-[9999] bg-primary text-primary-foreground px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          Skip to main content
        </a>
        <a
          href="#main-navigation"
          onClick={handleSkipToNav}
          className="fixed top-4 left-36 z-[9999] bg-primary text-primary-foreground px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          Skip to navigation
        </a>
      </div>

      {/* Live Region for Screen Reader Announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcements}
      </div>

      {/* Alert Region for Urgent Announcements */}
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
        id="urgent-announcements"
      />
    </>
  );
}

/**
 * Hook to announce messages to screen readers
 */
export function useScreenReaderAnnouncement() {
  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if ((window as any).announceToScreenReader) {
      (window as any).announceToScreenReader(message, priority);
    }
  };

  return { announce };
}
